/** CCG base review constraints only. Full known-value dependencies remain server-side. */
import {
  EBAY_PROFESSIONAL_GRADE_IDS,
  EBAY_PROFESSIONAL_GRADER_IDS,
  EBAY_RAW_CONDITION_IDS,
  PROFESSIONAL_GRADERS,
  PROFESSIONAL_GRADES,
  RAW_CARD_CONDITIONS,
  normalizeCardPresentation,
  cardPresentationLabel,
  type CardPresentation,
} from './card-presentation';
import { isEbayCategoryPolicyFresh, type EbayCategoryPolicy } from './ebay-category-policy';
import type { EbayItemSpecifics, EbaySellerHubSinglesCategory } from './ebay-seller-hub';

export const EBAY_CCG_SINGLES_HEADERS = [
  '*Action(SiteID=US|Country=US|Currency=USD|Version=1193|CC=UTF-8)',
  'CustomLabel',
  '*Category',
  'StoreCategory',
  '*Title',
  'Subtitle',
  'Relationship',
  'RelationshipDetails',
  'ScheduleTime',
  '*ConditionID',
  'CD:Professional Grader - (ID: 27501)',
  'CD:Grade - (ID: 27502)',
  'CDA:Certification Number - (ID: 27503)',
  'CD:Card Condition - (ID: 40001)',
  '*C:Game',
  'C:Card Name',
  'C:Character',
  'C:Card Type',
  'C:Age Level',
  'C:Set',
  'C:Speciality',
  'C:Rarity',
  'C:Features',
  'C:Finish',
  'C:Manufacturer',
  'C:Attribute/MTG:Color',
  'C:Creature/Monster Type',
  'C:Autographed',
  'C:Card Number',
  'C:Language',
  'C:Year Manufactured',
  'C:Card Size',
  'C:Stage',
  'C:Country of Origin',
  'C:Vintage',
  'C:Signed By',
  'C:Material',
  'C:Convention/Event',
  'C:Franchise',
  'C:Autograph Authentication',
  'C:Autograph Format',
  'C:Illustrator',
  'C:HP',
  'C:Attack/Power',
  'C:Defense/Toughness',
  'C:California Prop 65 Warning',
  'C:Cost',
  'C:Autograph Authentication Number',
  'C:Customized',
  'PicURL',
  'GalleryType',
  'VideoID',
  '*Description',
  '*Format',
  '*Duration',
  '*StartPrice',
  'BuyItNowPrice',
  'BestOfferEnabled',
  'BestOfferAutoAcceptPrice',
  'MinimumBestOfferPrice',
  '*Quantity',
  'ImmediatePayRequired',
  '*Location',
  'ShippingType',
  'ShippingService-1:Option',
  'ShippingService-1:Cost',
  'ShippingService-2:Option',
  'ShippingService-2:Cost',
  '*DispatchTimeMax',
  'PromotionalShippingDiscount',
  'ShippingDiscountProfileID',
  '*ReturnsAcceptedOption',
  'ReturnsWithinOption',
  'RefundOption',
  'ShippingCostPaidByOption',
  'AdditionalDetails',
  'ShippingProfileName',
  'ReturnProfileName',
  'PaymentProfileName',
  'ProductCompliancePolicyID',
  'Regional ProductCompliancePolicies',
  'Product Safety Pictograms',
  'Product Safety Statements',
  'Product Safety Component',
  'Regulatory Document Ids',
  'Manufacturer Name',
  'Manufacturer AddressLine1',
  'Manufacturer AddressLine2',
  'Manufacturer City',
  'Manufacturer Country',
  'Manufacturer PostalCode',
  'Manufacturer StateOrProvince',
  'Manufacturer Phone',
  'Manufacturer Email',
  'Manufacturer ContactURL',
  'Responsible Person 1',
  'Responsible Person 1 Type',
  'Responsible Person 1 AddressLine1',
  'Responsible Person 1 AddressLine2',
  'Responsible Person 1 City',
  'Responsible Person 1 Country',
  'Responsible Person 1 PostalCode',
  'Responsible Person 1 StateOrProvince',
  'Responsible Person 1 Phone',
  'Responsible Person 1 Email',
  'Responsible Person 1 ContactURL',
] as const;
export const EBAY_CCG_TEMPLATE_HEADERS_SHA256 =
  'e692acdff29828c4d55b5f079c05171007e48bbd9c01eb87b0e70ba0b80e7c82';
export const EBAY_CCG_TEMPLATE_SHA256 =
  'bc0c978bdcbccb7de1874a26774471352400c450e50f5df7cf4ce7fe8685b188';

// eslint-disable-next-line no-control-regex -- Provider values cannot contain C0/C1 controls.
const controls = /[\u0000-\u001f\u007f-\u009f]/;
const hash = (v: unknown): v is string => typeof v === 'string' && /^[a-f0-9]{64}$/.test(v);
const text = (v: unknown): v is string =>
  typeof v === 'string' && v.trim() === v && v.length > 0 && !controls.test(v);
const strings = (v: unknown): v is string[] =>
  Array.isArray(v) && v.length <= 1000 && v.every(text) && new Set(v).size === v.length;
const names = EBAY_CCG_SINGLES_HEADERS.flatMap((h) => /^\*?C:(.+)$/.exec(h)?.[1] ?? []);

/** Runtime validation does not turn browser-supplied policy into server authority. */
export function requireEbayCcgPublicPolicy(
  policy: EbayCategoryPolicy | undefined,
): EbayCategoryPolicy {
  const invalid = (): never => {
    throw new Error('Current CCG category policy is required.');
  };
  if (!policy || policy.evidence !== 'POINT_IN_TIME_PUBLIC_POLICY') return invalid();
  try {
    if (new TextEncoder().encode(JSON.stringify(policy)).length > 64 * 1024) return invalid();
  } catch {
    return invalid();
  }
  const b = policy.binding;
  if (
    !b ||
    b.schemaVersion !== 1 ||
    b.parserVersion !== 1 ||
    !['PRODUCTION', 'SANDBOX'].includes(b.environment) ||
    b.marketplace !== 'EBAY_US' ||
    b.locale !== 'en-US' ||
    b.categoryId !== '183454' ||
    !text(b.categoryTreeId) ||
    !text(b.categoryTreeVersion) ||
    !hash(b.semanticDigest) ||
    b.templateSha256 !== EBAY_CCG_TEMPLATE_SHA256 ||
    b.templateHeadersSha256 !== EBAY_CCG_TEMPLATE_HEADERS_SHA256 ||
    !b.rawSha256 ||
    !Object.values(b.rawSha256).every(hash) ||
    !['tree', 'aspects', 'conditions'].every((k) => Object.hasOwn(b.rawSha256, k)) ||
    !isEbayCategoryPolicyFresh(b, Date.now())
  )
    return invalid();
  if (
    !Array.isArray(policy.aspects) ||
    policy.aspects.length !== names.length ||
    new Set(policy.aspects.map((a) => a?.name)).size !== names.length
  )
    return invalid();
  for (const a of policy.aspects) {
    if (
      !a ||
      !names.includes(a.name) ||
      !['STRING', 'NUMBER'].includes(a.dataType) ||
      (a.format !== undefined && a.format !== 'int32') ||
      (a.dataType === 'NUMBER' && (a.format !== 'int32' || a.mode !== 'SELECTION_ONLY')) ||
      (a.dataType === 'STRING' && a.format !== undefined) ||
      (a.applicableTo !== undefined &&
        (!strings(a.applicableTo) ||
          a.applicableTo.length < 1 ||
          a.applicableTo.length > 2 ||
          a.applicableTo.some((v) => v !== 'ITEM' && v !== 'PRODUCT'))) ||
      !['SINGLE', 'MULTI'].includes(a.cardinality) ||
      !['FREE_TEXT', 'SELECTION_ONLY'].includes(a.mode) ||
      typeof a.required !== 'boolean' ||
      !['OPTIONAL', 'RECOMMENDED'].includes(a.usage) ||
      typeof a.enabledForVariations !== 'boolean' ||
      typeof a.hasValueDependencies !== 'boolean' ||
      typeof a.suggestionsTruncated !== 'boolean' ||
      !strings(a.suggestions) ||
      (a.maxLength !== undefined &&
        (!Number.isSafeInteger(a.maxLength) || a.maxLength < 1 || a.maxLength > 4000)) ||
      (a.mode === 'SELECTION_ONLY' &&
        (!strings(a.selectionValues) || a.selectionValues.length === 0))
    )
      return invalid();
  }
  if (!policy.aspects.some((a) => a.name === 'Game' && a.required)) return invalid();
  const c = policy.conditions;
  if (
    !c ||
    c.rawConditionId !== '4000' ||
    c.gradedConditionId !== '2750' ||
    !Number.isSafeInteger(c.certificationMaxLength) ||
    c.certificationMaxLength < 1 ||
    c.certificationMaxLength > 4000 ||
    typeof c.certificationRequired !== 'boolean' ||
    !Array.isArray(c.raw) ||
    c.raw.length !== 4 ||
    new Set(c.raw.map((v) => v?.condition)).size !== 4 ||
    new Set(c.raw.map((v) => v?.valueId)).size !== 4 ||
    c.raw.some(
      (v) =>
        !v ||
        !RAW_CARD_CONDITIONS.includes(v.condition) ||
        v.valueId !==
          (
            {
              NEAR_MINT_OR_BETTER: '400010',
              EXCELLENT: '400015',
              VERY_GOOD: '400016',
              POOR: '400017',
            } as Record<string, string>
          )[v.condition] ||
        !text(v.label),
    ) ||
    !Array.isArray(c.graded) ||
    c.graded.length < 1 ||
    c.graded.length > 6 ||
    new Set(c.graded.map((v) => v?.grader)).size !== c.graded.length
  )
    return invalid();
  for (const g of c.graded) {
    if (
      !g ||
      !PROFESSIONAL_GRADERS.includes(g.grader) ||
      g.valueId !== EBAY_PROFESSIONAL_GRADER_IDS[g.grader] ||
      !Array.isArray(g.grades) ||
      g.grades.length < 1 ||
      g.grades.length > 19 ||
      new Set(g.grades.map((v) => v?.grade)).size !== g.grades.length ||
      new Set(g.grades.map((v) => v?.valueId)).size !== g.grades.length ||
      g.grades.some(
        (v) => !v || !PROFESSIONAL_GRADES.includes(v.grade as never) || !/^\d+$/.test(v.valueId),
      )
    )
      return invalid();
  }
  return policy;
}

/** Base constraints only: FREE_TEXT suggestions are not a closed enum. */
export function validateEbayCcgPublicSpecifics(
  policy: EbayCategoryPolicy | undefined,
  values: EbayItemSpecifics | undefined,
): string[] {
  const p = requireEbayCcgPublicPolicy(policy);
  const errors: string[] = [];
  if (
    values === null ||
    (values !== undefined && (typeof values !== 'object' || Array.isArray(values)))
  )
    return ['Invalid itemSpecifics.'];
  const specifics = values ?? {};
  for (const name of Object.keys(specifics)) {
    if (!p.aspects.some((a) => a.name === name))
      errors.push('Item specific name is not supported by this category.');
  }
  for (const a of p.aspects) {
    const v = Object.hasOwn(specifics, a.name) ? specifics[a.name] : undefined;
    const invalid = (reason: string): void => {
      errors.push(`Item specific "${a.name}" ${reason}.`);
    };
    if (v === undefined) {
      if (a.required) invalid('is required');
      continue;
    }
    if (
      !Array.isArray(v) ||
      v.length < 1 ||
      v.length > (a.cardinality === 'SINGLE' ? 1 : 30) ||
      new Set(v).size !== v.length
    ) {
      invalid('has invalid cardinality');
      continue;
    }
    for (const value of v) {
      if (!text(value) || value.length > (a.maxLength ?? 200) || value.includes('|'))
        invalid('has an invalid value');
      else if (a.mode === 'SELECTION_ONLY' && !a.selectionValues?.includes(value))
        invalid('must use a supported selection');
    }
  }
  return errors;
}

/** Category-specific provider descriptors; internal presentation and vision grading are untouched. */
export function mapEbayCategoryPresentation(
  category: EbaySellerHubSinglesCategory,
  presentation: CardPresentation | undefined,
  policy?: EbayCategoryPolicy,
): { conditionId: string; descriptors: Record<string, string> } {
  if (!['SPORT_SINGLES', 'NON_SPORT_SINGLES', 'CCG_SINGLES'].includes(category))
    throw new Error('Unsupported Seller Hub category.');
  const normalized = normalizeCardPresentation(presentation);
  if (!normalized) throw new Error('Card presentation must be classified as Raw or Graded.');
  if (category !== 'CCG_SINGLES') {
    return normalized.kind === 'RAW'
      ? {
          conditionId: '4000',
          descriptors: { '40001': EBAY_RAW_CONDITION_IDS[normalized.condition] },
        }
      : {
          conditionId: '2750',
          descriptors: {
            '27501': EBAY_PROFESSIONAL_GRADER_IDS[normalized.grader],
            '27502': EBAY_PROFESSIONAL_GRADE_IDS[normalized.grade],
            ...(normalized.certificationNumber === undefined
              ? {}
              : { '27503': normalized.certificationNumber }),
          },
        };
  }
  const p = requireEbayCcgPublicPolicy(policy);
  if (normalized.kind === 'RAW') {
    const raw = p.conditions.raw.find((v) => v.condition === normalized.condition);
    if (!raw) throw new Error('Unsupported CCG raw condition.');
    return { conditionId: p.conditions.rawConditionId, descriptors: { '40001': raw.valueId } };
  }
  const grader = p.conditions.graded.find((v) => v.grader === normalized.grader);
  const grade = grader?.grades.find((v) => v.grade === normalized.grade);
  if (!grader || !grade) throw new Error('Unsupported CCG grader and grade pair.');
  // Check the untrimmed input before normalization: excess characters must never be hidden.
  const cert = presentation?.kind === 'GRADED' ? presentation.certificationNumber : undefined;
  if (
    (p.conditions.certificationRequired && cert === undefined) ||
    (cert !== undefined &&
      (!text(cert.trim()) ||
        controls.test(cert) ||
        cert.length > Math.min(30, p.conditions.certificationMaxLength)))
  )
    throw new Error('Invalid CCG certification number.');
  return {
    conditionId: p.conditions.gradedConditionId,
    descriptors: {
      '27501': grader.valueId,
      '27502': grade.valueId,
      ...(cert === undefined ? {} : { '27503': cert.trim() }),
    },
  };
}

/** CCG uses provider category labels while retaining the unchanged internal presentation. */
export function ebayCategoryPresentationLabel(
  category: EbaySellerHubSinglesCategory,
  presentation: CardPresentation | undefined,
  policy?: EbayCategoryPolicy,
): string {
  mapEbayCategoryPresentation(category, presentation, policy);
  if (category === 'CCG_SINGLES' && presentation?.kind === 'RAW') {
    return `Raw · ${requireEbayCcgPublicPolicy(policy).conditions.raw.find((v) => v.condition === presentation.condition)!.label}`;
  }
  return cardPresentationLabel(presentation);
}
